import { Check, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { LoadingStage } from "@/lib/drawing-viewer";

export type LoadingStep = "download" | "process" | "render" | "sync";

export const LOADING_STEPS: readonly LoadingStep[] = [
	"download",
	"process",
	"render",
	"sync",
] as const;

export const STAGE_TO_STEP: Record<LoadingStage, LoadingStep> = {
	initializing: "download",
	// Manager/renderer construction happens BEFORE the data download, so this
	// stage must map to the first step — mapping it to "render" would make the
	// stepper jump ahead and then reset when "fetchingData" fires.
	initializingViewer: "download",
	fetchingData: "download",
	decompressing: "process",
	processingImages: "process",
	processingText: "process",
	initializingRenderer: "render",
	syncingCollaboration: "sync",
	// `loadingComplete` is never displayed (overlay unmounts before it fires),
	// but map it to the last step so the stepper renders fully complete if it does.
	loadingComplete: "sync",
};

interface PlannerDrawingLoadingOverlayProps {
	stage: LoadingStage;
}

/**
 * Fullscreen loading overlay for the planner drawing viewer.
 * Shows a 4-step stepper with the active step spinning — mirrors the
 * mobile drawing viewer's loading overlay in apps/web.
 */
export function PlannerDrawingLoadingOverlay({
	stage,
}: PlannerDrawingLoadingOverlayProps): React.ReactElement {
	const { t } = useTranslation();
	const currentStep = STAGE_TO_STEP[stage];
	const currentIndex = LOADING_STEPS.indexOf(currentStep);

	return (
		<div className="absolute inset-0 z-40 flex flex-col items-center justify-center bg-background">
			<div className="flex w-full max-w-sm flex-col items-center gap-6 px-8">
				<h2 className="text-center font-semibold text-lg">
					{t("viewer.loadingProgress.title")}
				</h2>

				<div className="flex w-full items-center">
					{LOADING_STEPS.map((step, index) => {
						const isComplete = index < currentIndex;
						const isActive = index === currentIndex;
						return (
							<div key={step} className="flex flex-1 items-center last:flex-0">
								<StepIndicator
									step={step}
									state={
										isComplete ? "complete" : isActive ? "active" : "pending"
									}
								/>
								{index < LOADING_STEPS.length - 1 && (
									<div
										className={`mx-1 h-0.5 flex-1 ${
											isComplete ? "bg-dv-mobile-bg" : "bg-dv-mobile-bg/20"
										}`}
									/>
								)}
							</div>
						);
					})}
				</div>

				<p className="text-center text-muted-foreground text-sm">
					{t(`viewer.loadingProgress.steps.${currentStep}`)}
				</p>
			</div>
		</div>
	);
}

interface StepIndicatorProps {
	step: LoadingStep;
	state: "complete" | "active" | "pending";
}

function StepIndicator({
	step,
	state,
}: StepIndicatorProps): React.ReactElement {
	if (state === "complete") {
		return (
			<div
				data-testid={`loading-step-${step}`}
				data-state={state}
				className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-dv-mobile-bg text-dv-mobile-text"
			>
				<Check className="h-4 w-4" />
			</div>
		);
	}
	if (state === "active") {
		return (
			<div
				data-testid={`loading-step-${step}`}
				data-state={state}
				className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-dv-mobile-bg text-dv-mobile-bg"
			>
				<Loader2 className="h-4 w-4 animate-spin" />
			</div>
		);
	}
	return (
		<div
			data-testid={`loading-step-${step}`}
			data-state={state}
			className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-dv-mobile-bg/20 bg-background"
		/>
	);
}
